import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { toConnectableService } from '~publish/helpers/channelConnections'
import type { ConnectableService } from '~publish/helpers/channelConnections'
import { channelServices } from '~publish/helpers/channels'

export type GuestNetworkId =
  | 'instagram'
  | 'x'
  | 'linkedin'
  | 'facebook-page'
  | 'tiktok'
  | 'threads'
  | 'youtube'
  | 'pinterest'
  | 'bluesky'
  | 'mastodon'
  | 'googlebusiness'
  | 'substack'

export type GuestNetwork = Readonly<{
  id: GuestNetworkId
  name: string
  icon: Service | 'x'
}>

export const GUEST_NETWORKS: readonly GuestNetwork[] = [
  { id: 'x', name: 'X', icon: 'x' },
  { id: 'facebook-page', name: 'Facebook Page', icon: 'facebook' },
  { id: 'instagram', name: 'Instagram', icon: 'instagram' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'linkedin' },
  {
    id: 'googlebusiness',
    name: 'Google Business Profile',
    icon: 'googlebusiness',
  },
  { id: 'pinterest', name: 'Pinterest', icon: 'pinterest' },
  { id: 'tiktok', name: 'TikTok', icon: 'tiktok' },
  { id: 'mastodon', name: 'Mastodon', icon: 'mastodon' },
  { id: 'youtube', name: 'YouTube', icon: 'youtube' },
  { id: 'threads', name: 'Threads', icon: 'threads' },
  { id: 'bluesky', name: 'Bluesky', icon: 'bluesky' },
  { id: 'substack', name: 'Substack', icon: 'substack' },
]

export function getGuestNetworkService(network: GuestNetwork): Service {
  return network.icon === 'x' ? 'twitter' : network.icon
}

const GUEST_NETWORK_IDS: ReadonlySet<string> = new Set(
  GUEST_NETWORKS.map((network) => network.id),
)

const LEGACY_FACEBOOK_GROUP_ID = 'facebook-group'

function isGuestNetworkId(value: unknown): value is GuestNetworkId {
  return typeof value === 'string' && GUEST_NETWORK_IDS.has(value)
}

export function getGuestNetworkIdForService(
  service: string,
): GuestNetworkId | undefined {
  return GUEST_NETWORKS.find(
    (network) => getGuestNetworkService(network) === service,
  )?.id
}

export function normalizeGuestNetworks(
  value: unknown,
): readonly GuestNetworkId[] {
  if (!Array.isArray(value)) return []

  const selected = new Set(
    value
      .map((id) => (id === LEGACY_FACEBOOK_GROUP_ID ? 'facebook-page' : id))
      .filter(isGuestNetworkId),
  )
  return GUEST_NETWORKS.map((network) => network.id).filter((id) =>
    selected.has(id),
  )
}

export function haveEqualGuestNetworks(
  left: readonly GuestNetworkId[] | undefined,
  right: readonly GuestNetworkId[] | undefined,
): boolean {
  const leftNetworks = left ?? []
  const rightNetworks = right ?? []
  return (
    leftNetworks.length === rightNetworks.length &&
    leftNetworks.every((network, index) => network === rightNetworks[index])
  )
}

const GUEST_UNCONNECTABLE_SERVICES: ReadonlySet<Service> = new Set([
  'startPage',
  'whatsapp',
])

export const GUEST_CONNECTABLE_SERVICES: readonly Service[] =
  channelServices.filter(
    (service) => !GUEST_UNCONNECTABLE_SERVICES.has(service),
  )

const CONNECT_CHANNEL_HASH_PREFIX = '#connectChannel='

export function getGuestConnectChannelPath(service: Service): string {
  return `/calendar/week${CONNECT_CHANNEL_HASH_PREFIX}${service}`
}

export function getGuestConnectChannelService(
  hash: string,
): ConnectableService | null {
  if (!hash.startsWith(CONNECT_CHANNEL_HASH_PREFIX)) return null
  return toConnectableService(hash.slice(CONNECT_CHANNEL_HASH_PREFIX.length))
}
